/**
 * Générateur — Automatisme : calculer une distance réelle à partir d'une
 * distance sur une carte d'échelle connue.
 * Attendu ID : 1253 | Niveau : 3ème | Domaine : Proportionnalité
 * Rubrique : AUTOMATISME (programme 2025, cycle 4 page 19, lignes 1390-1391 :
 * « Calculer la distance réelle entre deux villes, connaissant la distance
 * entre ces villes sur une carte routière dont l'échelle est connue. »).
 *
 * ── Périmètre (cadrage programme-maths 2026-09-12, mode auto) ─────────────
 * Le SENS DIRECT seul : de la carte au réel. Sept échelles de cartes
 * routières : 1/25 000, 1/50 000, 1/100 000, 1/200 000, 1/250 000,
 * 1/500 000, 1/1 000 000 ; d = 2..30 cm (un demi-centimètre un quart des
 * fois) ; réponse en km (en m pour 1/25 000 et 1/50 000), toujours exacte.
 * Hors : le sens inverse (la longueur sur la carte), les maquettes et
 * agrandissements (5DC2 options 6-8, gen_174), « 2 cm représentent 10 km »
 * (gen_172), le calcul d'une échelle (choix mode auto, À ARBITRER : sens
 * inverse, 1/200 000, villes nommées ou A et B).
 * Banque MathsMentales (S. Cogez, Apache 2.0, relevé 2026-09-12) : 5DC2
 * options 0-5 (cartes au 1/1 000 000 … 1/25 000, d 0,5..30 cm en dixièmes,
 * les deux sens) ; retenu : les échelles, d en demi-centimètres.
 *
 * ── Plan de cartes ─────────────────────────────────────────────────────────
 *   Carte 1 « Série flash » (flash_texte_trou_saisie) : trois lignes « Carte
 *     au 1/500 000 : 4 cm sur la carte, c'est ⬚ km en réalité. », échelles
 *     distinctes.
 *   Carte 2 « La distance réelle » (distance_qcm_unique) : « Sur une carte au
 *     1/500 000, deux villes sont à 4 cm l'une de l'autre. En réalité, elles
 *     sont à… » [20 km ; 2 km ; 200 km ; 4 km] ; contenu.sousType = l'échelle.
 *   Carte 3 « Vrai ou faux » (sens_tableau_vf) : quatre lignes « Sur une
 *     carte au 1/100 000, 1 cm représente 1 km. » (V) ; « …, 3 cm
 *     représentent 30 km. » (F).
 *
 * INVARIANTS : tout en MÈTRES entiers (d en demi-centimètres × échelle ÷
 * 200, toujours entier : assert par un second chemin) ; jamais de flottant
 * ; distracteurs distincts de la clé et entre eux.
 * SACRIFIÉ (fiche : le tableau des unités de longueur, « 500 000 cm, c'est
 * 5 km ») : (4 cm ; 1/500 000) ; auto-testé par gen1253.self.test.js.
 *
 * ── Erreurs (familles nommées ; aucun item DEPP) ─────────────────────────
 *   rang_faux        : un rang de trop ou de moins (2 km, 200 km ; nom de
 *                      1102/1104), PRINCIPALE ;
 *   un_cm_un_km      : 1 cm = 1 km quelle que soit l'échelle (4 km) ;
 *   sans_conversion  : 2 000 000 cm lu 2 000 km (les cm pris pour des km) ;
 *   sens_inverse     : la distance divisée au lieu de multipliée ;
 *   chiffres_de_tete : 4 × 5 = 20 recopié sans l'unité (20 m pour 20 km).
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeTexteTrou,
} from '../../../utils';

const ATTENDU_ID = 1253;
const opts = (label) => ({ niveau: '3eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_FLASH = 'Série flash';
const L_QCM = 'La distance réelle';
const L_VF = 'Vrai ou faux';

// ─── Le domaine (tout en mètres entiers) ──────────────────────────────────

const ECHELLES = [25000, 50000, 100000, 200000, 250000, 500000, 1000000];
/** L'exemple de la fiche : 4 cm au 1/500 000 (20 km). */
export const SACRIFIE = { d: 4, echelle: 500000 };

/** « 1/500000 » en brut : l'espace des milliers est une règle d'affichage (utils/milliers.js), jamais posée par un gen. */
const echelleTexte = (e) => `1/${e}`;
/** Les mètres réels pour d demi-centimètres à l'échelle e (assert : deux chemins). */
const metres = (dHalf, e) => {
    const m1 = (dHalf * e) / 200;
    const m2 = ((dHalf / 2) * e) / 100;
    if (!Number.isInteger(m1) || m1 !== m2) throw new Error(`gen_1253 : ${dHalf} ${e}`);
    return m1;
};
/** L'unité de la réponse : m pour 1/25 000 et 1/50 000, km sinon. */
const uniteDe = (e) => (e <= 50000 ? 'm' : 'km');
/** Un nombre de mètres écrit dans l'unité, à la française (« 7,5 »). */
const ecrire = (m, unite) => {
    if (unite === 'm') return String(m);
    const km = m / 1000;
    return Number.isInteger(km) ? String(km) : String(km).replace('.', ',');
};
const cmTexte = (dHalf) => (dHalf % 2 === 0 ? String(dHalf / 2) : `${(dHalf - 1) / 2},5`);

/** Un item { echelle, dHalf, m, unite } ; d = 2..30 cm, demi-centimètre un quart des fois (échelles ≥ 100 000 seulement, pour rester en km lisibles). */
const tirer = ({ exclure = [] } = {}) => {
    for (let essai = 0; essai < 200; essai++) {
        const echelle = randomChoice(ECHELLES);
        const demi = randInt(1, 4) === 1 && echelle >= 100000;
        // en mètres (1/25 000, 1/50 000) : d ≤ 12 cm, la réponse reste un nombre de centaines de mètres lisible
        const dHalf = demi ? 2 * randInt(2, 29) + 1 : 2 * randInt(2, echelle <= 50000 ? 12 : 30);
        if (dHalf === 2 * SACRIFIE.d && echelle === SACRIFIE.echelle) continue;
        if (exclure.includes(echelle)) continue;
        const m = metres(dHalf, echelle);
        const unite = uniteDe(echelle);
        if (unite === 'km' && m % 100 !== 0) continue; // au plus une décimale en km
        return { echelle, dHalf, m, unite };
    }
    return { echelle: 100000, dHalf: 14, m: 7000, unite: 'km' };
};

const EXPLICATIONS = {
    rang_faux: '1/500 000 : 1 cm sur la carte, c\'est 500 000 cm en réalité, soit 5 km. 4 cm, c\'est 4 × 5 = 20 km.',
    un_cm_un_km: '1 cm ne vaut 1 km que sur une carte au 1/100 000. Je lis l\'échelle : 1/500 000, c\'est 5 km par cm.',
    sans_conversion: '2 000 000, ce sont des cm : je convertis, 2 000 000 cm = 20 000 m = 20 km.',
    sens_inverse: 'La distance réelle est plus grande que sur la carte : je multiplie par l\'échelle.',
    chiffres_de_tete: 'Les chiffres sont bons, l\'unité compte : 4 × 500 000 cm = 20 km, pas 20 m.',
    regle: 'Je convertis l\'échelle en « 1 cm = … » : 1/100 000, c\'est 1 cm pour 100 000 cm = 1 km. Puis je multiplie par la distance sur la carte.',
};

// ─── Distracteurs (en mètres) ─────────────────────────────────────────────

const candidats = (it) => {
    const { m, dHalf, echelle } = it;
    const c = [];
    const pousser = (code, v) => { if (Number.isInteger(v) && v > 0 && v !== m && !c.some((x) => x.m === v)) c.push({ code, m: v }); };
    pousser('rang_faux', m / 10);
    pousser('rang_faux', m * 10);
    pousser('un_cm_un_km', (dHalf / 2) * 1000);
    pousser('sans_conversion', (dHalf / 2) * echelle * 10); // d × échelle « cm » lus en km → en mètres
    if (m % 100 === 0) pousser('sens_inverse', m / 100);
    return c.filter((x) => (it.unite === 'm' ? Number.isInteger(x.m) : x.m % 100 === 0));
};
const distracteurs = (it) => {
    const pris = [];
    const cands = shuffle(candidats(it));
    for (const x of [...cands.filter((x) => x.code === 'rang_faux').slice(0, 1), ...cands.filter((x) => x.code !== 'rang_faux')]) if (!pris.some((y) => y.code === x.code)) pris.push(x);
    return pris.length >= 3 ? pris.slice(0, 3) : null;
};

// ─── Carte 1 : Série flash ────────────────────────────────────────────────

const ligneFlash = (it) => `Carte au ${echelleTexte(it.echelle)} : ${cmTexte(it.dHalf)} cm sur la carte, c'est {{${ecrire(it.m, it.unite)}}} ${it.unite} en réalité.`;

const genFlash = () => {
    for (let essai = 0; essai < 100; essai++) {
        const items = [];
        for (let i = 0; i < 3; i++) items.push(tirer({ exclure: items.map((it) => it.echelle) }));
        if (new Set(items.map((it) => it.echelle)).size < 3) continue;
        const payload = makeTexteTrou(items.map(ligneFlash).join('\n'), '<p><b>Complète.</b></p>', { ...opts(L_FLASH), mode: 'saisie', strict: false });
        payload.contenu = { ...payload.contenu, explications: { regle: EXPLICATIONS.regle, rang_faux: EXPLICATIONS.rang_faux } };
        payload._meta.params = { items };
        return payload;
    }
    const items = [{ echelle: 100000, dHalf: 14, m: 7000, unite: 'km' }, { echelle: 250000, dHalf: 8, m: 10000, unite: 'km' }, { echelle: 50000, dHalf: 6, m: 1500, unite: 'm' }];
    const payload = makeTexteTrou(items.map(ligneFlash).join('\n'), '<p><b>Complète.</b></p>', { ...opts(L_FLASH), mode: 'saisie', strict: false });
    payload.contenu = { ...payload.contenu, explications: { regle: EXPLICATIONS.regle } };
    payload._meta.params = { items };
    return payload;
};

// ─── Carte 2 : La distance réelle (QCM) ───────────────────────────────────

const genQcm = () => {
    for (let essai = 0; essai < 200; essai++) {
        const it = tirer();
        const faux = distracteurs(it);
        if (!faux) continue;
        const bon = `${ecrire(it.m, it.unite)} ${it.unite}`;
        const choix = [bon, ...faux.map((f) => `${ecrire(f.m, it.unite)} ${it.unite}`)];
        if (new Set(choix).size !== 4) continue;
        const stem = `<p>Sur une carte au ${echelleTexte(it.echelle)}, deux villes sont à ${cmTexte(it.dHalf)} cm l'une de l'autre.</p><p><b>En réalité, elles sont à…</b></p>`;
        const payload = makeQcm(choix, bon, stem, opts(L_QCM));
        payload.contenu = { ...payload.contenu, sousType: String(it.echelle), explications: EXPLICATIONS };
        payload._meta.params = { ...it, distracteurs: faux.map(({ code }) => ({ code })) };
        return payload;
    }
    // Repli vérifié : 7 cm au 1/100 000 = 7 km ; 70 km (rang), 0,7 km (rang), 700 km (sans_conversion).
    const it = { echelle: 100000, dHalf: 14, m: 7000, unite: 'km' };
    const payload = makeQcm(['7 km', '70 km', '0,7 km', '700 km'], '7 km', `<p>Sur une carte au ${echelleTexte(100000)}, deux villes sont à 7 cm l'une de l'autre.</p><p><b>En réalité, elles sont à…</b></p>`, opts(L_QCM));
    payload.contenu = { ...payload.contenu, sousType: '100000', explications: EXPLICATIONS };
    payload._meta.params = { ...it, distracteurs: [{ code: 'rang_faux' }, { code: 'rang_faux' }, { code: 'sans_conversion' }] };
    return payload;
};

// ─── Carte 3 : Vrai ou faux ───────────────────────────────────────────────

const genVf = () => {
    for (let essai = 0; essai < 200; essai++) {
        const items = [];
        for (let i = 0; i < 4; i++) items.push(tirer({ exclure: items.map((it) => it.echelle) }));
        if (new Set(items.map((it) => it.echelle)).size < 4) continue;
        const nbVraies = randomChoice([1, 2, 2, 3]);
        const verites = shuffle([...Array(nbVraies).fill(true), ...Array(4 - nbVraies).fill(false)]);
        const lignes = items.map((it, i) => {
            const pluriel = it.dHalf > 2 ? 'représentent' : 'représente';
            if (verites[i]) return { ligne: `Sur une carte au ${echelleTexte(it.echelle)}, ${cmTexte(it.dHalf)} cm ${pluriel} ${ecrire(it.m, it.unite)} ${it.unite}.`, code: null };
            const cands = candidats(it);
            if (!cands.length) return null;
            const x = randomChoice(cands);
            return { ligne: `Sur une carte au ${echelleTexte(it.echelle)}, ${cmTexte(it.dHalf)} cm ${pluriel} ${ecrire(x.m, it.unite)} ${it.unite}.`, code: x.code };
        });
        if (lignes.some((l) => !l)) continue;
        const payload = makeVraiFaux(lignes.map((l) => l.ligne), verites.map((v) => (v ? 0 : 1)), '<p><b>Vrai ou faux ?</b></p>', opts(L_VF));
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
        payload._meta.params = { items, verites, distracteurs: lignes.filter((l) => l.code).map(({ code }) => ({ code })) };
        return payload;
    }
    const lignes = [`Sur une carte au ${echelleTexte(100000)}, 7 cm représentent 7 km.`, `Sur une carte au ${echelleTexte(250000)}, 4 cm représentent 1 km.`, `Sur une carte au ${echelleTexte(50000)}, 3 cm représentent 1500 m.`, `Sur une carte au ${echelleTexte(1000000)}, 3 cm représentent 3 km.`];
    const payload = makeVraiFaux(lignes, [0, 1, 0, 1], '<p><b>Vrai ou faux ?</b></p>', opts(L_VF));
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    payload._meta.params = { items: [], verites: [true, false, true, false], distracteurs: [{ code: 'rang_faux' }, { code: 'un_cm_un_km' }] };
    return payload;
};

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'tout en mètres entiers ; réponses en km (au plus une décimale) ou en m pour 1/25 000 et 1/50 000 ; quatre choix distincts',
        'sept échelles de cartes routières, d de 2 à 30 cm (demi-centimètre un quart des fois) ; sens direct seul',
        'sacrifié : 4 cm au 1/500 000',
    ],
    distance_qcm_unique: 'rang_faux (÷ 10 ou × 10, la principale, une seule des deux), un_cm_un_km, sans_conversion (les cm lus en km), sens_inverse',
    vf_tableau_vf: 'quatre échelles distinctes, les fausses tirent une famille',
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    flash_texte_trou_saisie: genFlash,
    distance_qcm_unique: genQcm,
    sens_tableau_vf: genVf,
};

export const CATEGORIES = [
    { id: 'flash', label: L_FLASH, formatsList: [{ key: 'flash_texte_trou_saisie', label: 'Saisie trous' }] },
    { id: 'distance', label: L_QCM, formatsList: [{ key: 'distance_qcm_unique', label: 'QCM' }] },
    { id: 'sens', label: L_VF, formatsList: [{ key: 'sens_tableau_vf', label: 'Vrai/Faux' }] },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
